import { SkeletonImage } from '@smartlinks/react-design-system/dist/src/lib/ui-elements/skeleton/skeleton-image';
import { SkeletonText } from '@smartlinks/react-design-system/dist/src/lib/ui-elements/skeleton/skeleton-text';
import { CSSProperties } from 'react';

export interface SearchWidgetSkeletonFieldInterface {
	id: string;
	flex?: number;
	minWidth?: number;
}

export interface SearchWidgetSkeletonProps {
	testIdPrefix: string;
	fields: SearchWidgetSkeletonFieldInterface[];
	isFadingOut?: boolean;
	controlHeight?: number;
	submitWidth?: number;
	showLabels?: boolean;
}

const DEFAULT_CONTROL_HEIGHT = 48;
const DEFAULT_SUBMIT_WIDTH = 120;
const skeletonStyles = `
	.searchWidgetSkeleton {
		width: 100%;
		opacity: 1;
		transition: opacity 200ms ease-out;
	}

	.searchWidgetSkeleton--fade-out {
		opacity: 0;
	}

	.searchWidgetSkeleton__row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 12px;
		width: 100%;
		min-height: 120px;
		padding: 12px 0;
	}

	.searchWidgetSkeleton__field {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.searchWidgetSkeleton__submit {
		display: flex;
		align-items: flex-end;
	}

	@media (max-width: 768px) {
		.searchWidgetSkeleton__row {
			flex-direction: column;
			align-items: stretch;
		}

		.searchWidgetSkeleton__submit {
			align-items: stretch;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.searchWidgetSkeleton {
			transition: none;
		}
	}
`;

/**
 * Base reutilizable para los skeletons de formulario de los widgets de búsqueda.
 *
 * Renderiza una barra con un placeholder por control (input, select o datepicker)
 * más el botón de búsqueda, usando los primitivos shimmer de
 * `@smartlinks/react-design-system`. Cada producto compone esta base con la
 * configuración de campos propia de su formulario para conservar dimensiones
 * idénticas y mantener el CLS por debajo de 0.1.
 */
export function SearchWidgetSkeleton({
	testIdPrefix,
	fields,
	isFadingOut,
	controlHeight = DEFAULT_CONTROL_HEIGHT,
	submitWidth = DEFAULT_SUBMIT_WIDTH,
	showLabels = true,
}: Readonly<SearchWidgetSkeletonProps>): JSX.Element {
	const containerClassName: string = `searchWidgetSkeleton${isFadingOut ? ' searchWidgetSkeleton--fade-out' : ''}`;
	return (
		<div className={containerClassName} data-testid={`${testIdPrefix}.search-form-skeleton.container`} aria-hidden='true'>
			<style dangerouslySetInnerHTML={{ __html: skeletonStyles }} />
			<div className='searchWidgetSkeleton__row' data-testid={`${testIdPrefix}.search-form-skeleton.fields-container`}>
				{fields.map((field: SearchWidgetSkeletonFieldInterface) => {
					const fieldStyle: CSSProperties = {
						flex: field.flex ?? 1,
						minWidth: field.minWidth ?? 0,
					};

					return (
						<div
							key={field.id}
							className='searchWidgetSkeleton__field'
							style={fieldStyle}
							data-testid={`${testIdPrefix}.search-form-skeleton.field-${field.id}-container`}
						>
							{showLabels && (
								<SkeletonText lines={1} width='45%' height={12} data-testid={`${testIdPrefix}.search-form-skeleton.field-${field.id}-label`} />
							)}
							<SkeletonImage height={controlHeight} data-testid={`${testIdPrefix}.search-form-skeleton.field-${field.id}-control`} />
						</div>
					);
				})}
				<div className='searchWidgetSkeleton__submit' data-testid={`${testIdPrefix}.search-form-skeleton.submit-container`}>
					<SkeletonImage width={submitWidth} height={controlHeight} data-testid={`${testIdPrefix}.search-form-skeleton.submit-btn`} />
				</div>
			</div>
		</div>
	);
}

export default SearchWidgetSkeleton;
